import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import AuthLayout from './AuthLayout';

export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    let signInError = null;
    try {
      const res = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      signInError = res.error;
    } catch (thrown) {
      // Network/DNS failures come as thrown TypeError('Failed to fetch')
      signInError = thrown;
    }
    setLoading(false);
    if (signInError) {
      const msg = signInError.message || String(signInError);
      if (msg === 'Invalid login credentials') {
        setError('Correo o contraseña incorrectos.');
      } else if (/Failed to fetch|NetworkError|ERR_NAME_NOT_RESOLVED|network|dns/i.test(msg)) {
        setError('No se pudo conectar al servidor de autenticación. Verifica tu conexión o vuelve a intentar en unos minutos.');
      } else {
        setError(msg);
      }
      return;
    }
    navigate(from, { replace: true });
  };

  return (
    <AuthLayout
      title="Iniciar sesión"
      subtitle="Accede con tu correo institucional"
      footer={<Link to="/auth/reset" className="text-[#5346E6] font-semibold hover:underline" data-testid="link-reset-password">¿Olvidaste tu contraseña?</Link>}
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
        <label className="block text-sm font-semibold text-[#0B1B5E]">
          Correo institucional
          <div className="relative mt-1">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7B86A6]" aria-hidden />
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu.nombre@colegio.cl"
              className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] placeholder:text-[#9AA5C2] focus:border-[#5346E6] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#5346E6]/15 transition-colors text-sm"
              data-testid="input-email"
            />
          </div>
        </label>

        <label className="block text-sm font-semibold text-[#0B1B5E]">
          Contraseña
          <div className="relative mt-1">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7B86A6]" aria-hidden />
            <input
              type={showPwd ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ingresa tu contraseña"
              className="w-full pl-9 pr-10 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] placeholder:text-[#9AA5C2] focus:border-[#5346E6] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#5346E6]/15 transition-colors text-sm"
              data-testid="input-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7B86A6] hover:text-[#5346E6]"
              aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              data-testid="toggle-password-visibility"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[#FFE9E9] border border-[#FFC4C4] text-[#9B1C1C] text-xs" role="alert" data-testid="login-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl bg-[#0B1B5E] hover:bg-[#5346E6] disabled:bg-[#8089AE] text-white font-bold text-sm transition-colors shadow-[0_10px_24px_-10px_rgba(11,27,94,0.55)]"
          data-testid="login-submit"
        >
          {loading ? 'Verificando…' : 'Iniciar sesión'}
        </button>

        <p className="text-[11.5px] text-[#7B86A6] leading-relaxed text-center">
          Si es tu primer ingreso, deberás cambiar la contraseña que recibiste por correo.
        </p>
      </form>
    </AuthLayout>
  );
}
